import { useQuery, type UseQueryResult } from 'react-query'

import type { AgentCitationPost } from 'features/ask-yogi/types'

/**
 * Reads a single citation's preview data from the react-query cache. The
 * data is populated in one batched request per message by
 * `useCitationsData`, which seeds the `['citation', projectId, id]` key
 * this hook subscribes to. This hook never fetches on its own
 * (`enabled: false`) — that's what guarantees a message with N citations
 * makes exactly one request, not N.
 *
 * `data` is `undefined` while the batch is in flight (popover shows a
 * spinner), `null` once the batch resolves without this id (not found /
 * outside project — popover shows its fallback), or the post otherwise.
 */
export function useCitationData(
  projectId: string,
  citationId: string,
): UseQueryResult<AgentCitationPost | null> {
  return useQuery<AgentCitationPost | null>(
    ['citation', projectId, citationId],
    // Never invoked: enabled is false, so the value only ever comes from
    // the cache seeded by useCitationsData.
    () =>
      Promise.reject(new Error('citation data is seeded by useCitationsData')),
    {
      enabled: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  )
}
